import { Component, ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { hasError: boolean; message: string; isChunkError: boolean }

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : ''
  return (
    msg.includes('dynamically imported module') ||
    msg.includes('Failed to fetch') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed')
  )
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '', isChunkError: false }

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError:     true,
      message:      error instanceof Error ? error.message : 'An unexpected error occurred.',
      isChunkError: isChunkLoadError(error),
    }
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error)) {
      // Auto-reload once so users get the new bundle after a deployment.
      // Guard with sessionStorage to prevent infinite reload loops if the
      // chunk is genuinely missing (not just stale cache).
      const key = 'sprouthill_chunk_reload'
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, '1')
        window.location.reload()
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    if (this.state.isChunkError) {
      return (
        <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
            <div className="text-4xl mb-4 animate-spin" style={{ display: 'inline-block' }}>⟳</div>
            <h1 className="text-xl font-bold text-[#2C3E50] mb-2">New version available</h1>
            <p className="text-sm text-gray-500 mb-6">
              Sprout Hill was updated. Refreshing automatically…
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-[#1B6B3A] text-white rounded-lg text-sm font-semibold hover:bg-[#145230] transition-colors"
            >
              Refresh now
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-sm p-8 text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-[#2C3E50] mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-500 mb-6">
            Sprout Hill encountered an unexpected error. Please refresh the page to continue.
          </p>
          {this.state.message && (
            <p className="text-xs text-gray-400 font-mono bg-gray-50 rounded-lg p-3 mb-6 break-words">
              {this.state.message}
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-[#1B6B3A] text-white rounded-lg text-sm font-semibold hover:bg-[#145230] transition-colors"
          >
            Refresh page
          </button>
        </div>
      </div>
    )
  }
}
